import {
  IconAngle,
  IconBuildingStore,
  IconCertificate,
  IconChisel,
  IconWeight,
  IconPrinter,
  IconCut,
  IconDropletMinus,
  IconFence,
  IconLayersIntersect2,
  IconPlant2,
  IconReorder,
  IconWood,
  IconWash,
  IconFlask,
  IconShovelPitchforks,
} from "@tabler/icons-react"
import { IconProps, IconLamination, IconCoating } from "@tfg/ui"
import type { ParseKeys } from "i18next"
import React from "react"
import { useTranslation } from "react-i18next"

import { IconAppret } from "~/components/icons/activities/IconAppret"
import { IconComponentProvider } from "~/components/icons/activities/IconComponentProvider"
import { IconDyeing } from "~/components/icons/activities/IconDyeing"
import { IconFootwearAssembling } from "~/components/icons/activities/IconFootwearAssembling"
import { IconFootwearFinishing } from "~/components/icons/activities/IconFootwearFinishing"
import { IconManufacturing } from "~/components/icons/activities/IconManufacturing"
import { IconRawMaterial } from "~/components/icons/activities/IconRawMaterial"
import { IconSpinning } from "~/components/icons/activities/IconSpinning"
import { IconStitching } from "~/components/icons/activities/IconStitching"
import { IconWeaving } from "~/components/icons/activities/IconWeaving"

export const NO_ACTIVITY_NAME = "no-activity" as const

type ActivityTranslationScope = "default" | "uniqueActivitiesLabels"
type TraceabilityTranslationKey = ParseKeys<"traceability">

const ACTIVITY_TRANSLATION_KEYS = {
  accessoryProvider: {
    default: "activities.accessoryProvider",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.accessoryProvider",
  },
  accessoryRawMaterialProvider: {
    default: "activities.accessoryRawMaterialProvider",
    uniqueActivitiesLabels:
      "uniqueActivitiesLabels.accessoryRawMaterialProvider",
  },
  "apparel.duvet.baffle": {
    default: "activities.apparel.duvet.baffle",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.duvet.baffle",
  },
  "apparel.duvet.farm": {
    default: "activities.apparel.duvet.farm",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.duvet.farm",
  },
  "apparel.duvet.materialProduction": {
    default: "activities.apparel.duvet.materialProduction",
    uniqueActivitiesLabels:
      "uniqueActivitiesLabels.apparel.duvet.materialProduction",
  },
  "apparel.duvet.preProcessor": {
    default: "activities.apparel.duvet.preProcessor",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.duvet.preProcessor",
  },
  "apparel.fabric.appret": {
    default: "activities.apparel.fabric.appret",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.fabric.appret",
  },
  "apparel.fabric.coating": {
    default: "activities.apparel.fabric.coating",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.fabric.coating",
  },
  "apparel.fabric.dyeing": {
    default: "activities.apparel.fabric.dyeing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.fabric.dyeing",
  },
  "apparel.fabric.lamination": {
    default: "activities.apparel.fabric.lamination",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.fabric.lamination",
  },
  "apparel.fabric.materialProduction": {
    default: "activities.apparel.fabric.materialProduction",
    uniqueActivitiesLabels:
      "uniqueActivitiesLabels.apparel.fabric.materialProduction",
  },
  "apparel.fabric.spinning": {
    default: "activities.apparel.fabric.spinning",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.fabric.spinning",
  },
  "apparel.fabric.weavingKnitting": {
    default: "activities.apparel.fabric.weavingKnitting",
    uniqueActivitiesLabels:
      "uniqueActivitiesLabels.apparel.fabric.weavingKnitting",
  },
  "apparel.leather.appret": {
    default: "activities.apparel.leather.appret",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.leather.appret",
  },
  "apparel.leather.dyeing": {
    default: "activities.apparel.leather.dyeing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.leather.dyeing",
  },
  "apparel.leather.farm": {
    default: "activities.apparel.leather.farm",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.leather.farm",
  },
  "apparel.leather.materialProduction": {
    default: "activities.apparel.leather.materialProduction",
    uniqueActivitiesLabels:
      "uniqueActivitiesLabels.apparel.leather.materialProduction",
  },
  "apparel.leather.tanning": {
    default: "activities.apparel.leather.tanning",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.leather.tanning",
  },
  "apparel.manufacturing": {
    default: "activities.apparel.manufacturing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.manufacturing",
  },
  "apparel.yarn.dyeing": {
    default: "activities.apparel.yarn.dyeing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.yarn.dyeing",
  },
  "apparel.yarn.materialProduction": {
    default: "activities.apparel.yarn.materialProduction",
    uniqueActivitiesLabels:
      "uniqueActivitiesLabels.apparel.yarn.materialProduction",
  },
  "apparel.yarn.spinning": {
    default: "activities.apparel.yarn.spinning",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.apparel.yarn.spinning",
  },
  brand: {
    default: "activities.brand",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.brand",
  },
  certifiedEntity: {
    default: "activities.certifiedEntity",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.certifiedEntity",
  },
  "footwear.assembling": {
    default: "activities.footwear.assembling",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.footwear.assembling",
  },
  "footwear.finishing": {
    default: "activities.footwear.finishing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.footwear.finishing",
  },
  "footwear.stitching": {
    default: "activities.footwear.stitching",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.footwear.stitching",
  },
  "paper.assembling": {
    default: "activities.paper.assembling",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.paper.assembling",
  },
  "paper.bleaching": {
    default: "activities.paper.bleaching",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.paper.bleaching",
  },
  "paper.chemicalTreatment": {
    default: "activities.paper.chemicalTreatment",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.paper.chemicalTreatment",
  },
  "paper.laminating": {
    default: "activities.paper.laminating",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.paper.laminating",
  },
  "paper.manufacturing": {
    default: "activities.paper.manufacturing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.paper.manufacturing",
  },
  "paper.pressing": {
    default: "activities.paper.pressing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.paper.pressing",
  },
  "paper.printing": {
    default: "activities.paper.printing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.paper.printing",
  },
  "paper.resineRemoving": {
    default: "activities.paper.resineRemoving",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.paper.resineRemoving",
  },
  "paper.whiteWashing": {
    default: "activities.paper.whiteWashing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.paper.whiteWashing",
  },
  "wood.assembling": {
    default: "activities.wood.assembling",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.wood.assembling",
  },
  "wood.brushing": {
    default: "activities.wood.brushing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.wood.brushing",
  },
  "wood.calibrationAndCollage": {
    default: "activities.wood.calibrationAndCollage",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.wood.calibrationAndCollage",
  },
  "wood.harvesting": {
    default: "activities.wood.harvesting",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.wood.harvesting",
  },
  "wood.manufacturing": {
    default: "activities.wood.manufacturing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.wood.manufacturing",
  },
  "wood.materialDrying": {
    default: "activities.wood.materialDrying",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.wood.materialDrying",
  },
  "wood.materialPeeking": {
    default: "activities.wood.materialPeeking",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.wood.materialPeeking",
  },
  "wood.materialPeeling": {
    default: "activities.wood.materialPeeling",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.wood.materialPeeling",
  },
  "wood.materialSawing": {
    default: "activities.wood.materialSawing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.wood.materialSawing",
  },
  "wood.sanding": {
    default: "activities.wood.sanding",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.wood.sanding",
  },
  "product.washing": {
    default: "activities.product.washing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.product.washing",
  },
  "product.productWashing": {
    default: "activities.product.productWashing",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.product.productWashing",
  },
  noActivity: {
    default: "activities.noActivity",
    uniqueActivitiesLabels: "uniqueActivitiesLabels.noActivity",
  },
} as const satisfies Record<
  string,
  Record<ActivityTranslationScope, TraceabilityTranslationKey>
>

type ActivityTranslationSuffix = keyof typeof ACTIVITY_TRANSLATION_KEYS

export type UseGetOperationsOptions = {
  readonly translationScope?: ActivityTranslationScope
}

export const useGetOperations = (
  options: UseGetOperationsOptions = {}
): Record<
  string,
  {
    name: string
    icon: React.FC<IconProps> | null
  }
> => {
  const { translationScope = "default" } = options
  const { t } = useTranslation("traceability")
  const translate = (suffix: ActivityTranslationSuffix): string =>
    t(ACTIVITY_TRANSLATION_KEYS[suffix][translationScope])
  return {
    BRAND: {
      name: translate("brand"),
      icon: IconBuildingStore,
    },
    CERTIFIED_ENTITY: {
      name: translate("certifiedEntity"),
      icon: IconCertificate,
    },
    APPAREL_MANUFACTURING: {
      name: translate("apparel.manufacturing"),
      icon: IconManufacturing,
    },
    FABRIC_WEAVING_OR_KNITTING: {
      name: translate("apparel.fabric.weavingKnitting"),
      icon: IconWeaving,
    },
    FABRIC_DYEING_PRINTING: {
      name: translate("apparel.fabric.dyeing"),
      icon: IconDyeing,
    },
    FABRIC_SPINNING: {
      name: translate("apparel.fabric.spinning"),
      icon: IconSpinning,
    },
    FABRIC_APPRET: {
      name: translate("apparel.fabric.appret"),
      icon: IconAppret,
    },
    FABRIC_LAMINATION: {
      name: translate("apparel.fabric.lamination"),
      icon: IconLamination,
    },
    FABRIC_MATERIAL_PRODUCTION: {
      name: translate("apparel.fabric.materialProduction"),
      icon: IconRawMaterial,
    },
    YARN_PROVIDER: {
      name: translate("apparel.yarn.spinning"),
      icon: IconSpinning,
    },
    YARN_DYEING_PRINTING: {
      name: translate("apparel.yarn.dyeing"),
      icon: IconDyeing,
    },
    LEATHER_TANNING: {
      name: translate("apparel.leather.tanning"),
      icon: IconAppret,
    },
    YARN_MATERIAL_PRODUCTION: {
      name: translate("apparel.yarn.materialProduction"),
      icon: IconRawMaterial,
    },
    LEATHER_DYEING_PRINTING: {
      name: translate("apparel.leather.dyeing"),
      icon: IconDyeing,
    },
    LEATHER_MATERIAL_PRODUCTION: {
      name: translate("apparel.leather.materialProduction"),
      icon: IconFence,
    },
    LEATHER_APPRET: {
      name: translate("apparel.leather.appret"),
      icon: IconAppret,
    },
    LEATHER_FARM: {
      name: translate("apparel.leather.farm"),
      icon: IconShovelPitchforks,
    },
    FOOTWEAR_FINISHING: {
      name: translate("footwear.finishing"),
      icon: IconFootwearFinishing,
    },
    FOOTWEAR_STITCHING: {
      name: translate("footwear.stitching"),
      icon: IconStitching,
    },
    FOOTWEAR_ASSEMBLING: {
      name: translate("footwear.assembling"),
      icon: IconFootwearAssembling,
    },
    ACCESSORY_PROVIDER: {
      name: translate("accessoryProvider"),
      icon: IconComponentProvider,
    },
    ACCESSORY_RAW_MATERIAL_PROVIDER: {
      name: translate("accessoryRawMaterialProvider"),
      icon: IconRawMaterial,
    },
    DUVET_BAFFLE: {
      name: translate("apparel.duvet.baffle"),
      icon: null,
    },
    DUVET_PRE_PROCESSOR: {
      name: translate("apparel.duvet.preProcessor"),
      icon: null,
    },
    DUVET_FARM: {
      name: translate("apparel.duvet.farm"),
      icon: null,
    },
    DUVET_MATERIAL_PRODUCTION: {
      name: translate("apparel.duvet.materialProduction"),
      icon: IconRawMaterial,
    },
    WOOD_HARVESTING: {
      name: translate("wood.harvesting"),
      icon: IconPlant2,
    },
    WOOD_ASSEMBLING: {
      name: translate("wood.assembling"),
      icon: IconLayersIntersect2,
    },
    WOOD_MANUFACTURING: {
      name: translate("wood.manufacturing"),
      icon: IconManufacturing,
    },
    WOOD_BRUSHING: {
      name: translate("wood.brushing"),
      icon: IconPlant2,
    },
    WOOD_SANDING: {
      name: translate("wood.sanding"),
      icon: IconChisel,
    },
    WOOD_CALIBRATION_AND_COLLAGE: {
      name: translate("wood.calibrationAndCollage"),
      icon: IconAngle,
    },
    WOOD_MATERIAL_PEEKING: {
      name: translate("wood.materialPeeking"),
      icon: IconReorder,
    },
    WOOD_MATERIAL_PEELING: {
      name: translate("wood.materialPeeling"),
      icon: IconWood,
    },
    WOOD_MATERIAL_SAWING: {
      name: translate("wood.materialSawing"),
      icon: IconCut,
    },
    WOOD_MATERIAL_DRYING: {
      name: translate("wood.materialDrying"),
      icon: IconDropletMinus,
    },
    PAPER_ASSEMBLING: {
      name: translate("paper.assembling"),
      icon: IconLayersIntersect2,
    },
    PAPER_MANUFACTURING: {
      name: translate("paper.manufacturing"),
      icon: IconManufacturing,
    },
    PAPER_PRINTING: {
      name: translate("paper.printing"),
      icon: IconPrinter,
    },
    PAPER_PRESSING: {
      name: translate("paper.pressing"),
      icon: IconWeight,
    },
    PAPER_LAMINATING: {
      name: translate("paper.laminating"),
      icon: IconLamination,
    },
    PAPER_BLEACHING: {
      name: translate("paper.bleaching"),
      icon: IconPlant2,
    },
    PAPER_RESINE_REMOVING: {
      name: translate("paper.resineRemoving"),
      icon: IconPlant2,
    },
    PAPER_CHEMICAL_TREATMENT: {
      name: translate("paper.chemicalTreatment"),
      icon: IconFlask,
    },
    PAPER_WHITE_WASHING: {
      name: translate("paper.whiteWashing"),
      icon: IconWash,
    },
    COATING: {
      name: translate("apparel.fabric.coating"),
      icon: IconCoating,
    },
    WASHING: {
      name: translate("product.washing"),
      icon: IconWash,
    },
    "PRODUCT WASHING": {
      name: translate("product.productWashing"),
      icon: IconWash,
    },
    [NO_ACTIVITY_NAME]: {
      name: translate("noActivity"),
      icon: null,
    },
  }
}

export const useGetActivityTranslations = (activityName: string): string => {
  const activityTranslations = useGetOperations()
  return activityTranslations[activityName]?.name ?? activityName
}

export const useTraceabilityActivityTranslationAnchors = (): void => {
  const { t } = useTranslation(["traceability"])

  // t("traceability:activities.accessoryProvider")
  // t("traceability:activities.accessoryRawMaterialProvider")
  // t("traceability:activities.apparel.duvet.baffle")
  // t("traceability:activities.apparel.duvet.farm")
  // t("traceability:activities.apparel.duvet.materialProduction")
  // t("traceability:activities.apparel.duvet.preProcessor")
  // t("traceability:activities.apparel.fabric.appret")
  // t("traceability:activities.apparel.fabric.coating")
  // t("traceability:activities.apparel.fabric.dyeing")
  // t("traceability:activities.apparel.fabric.lamination")
  // t("traceability:activities.apparel.fabric.materialProduction")
  // t("traceability:activities.apparel.fabric.spinning")
  // t("traceability:activities.apparel.fabric.weavingKnitting")
  // t("traceability:activities.apparel.leather.appret")
  // t("traceability:activities.apparel.leather.dyeing")
  // t("traceability:activities.apparel.leather.farm")
  // t("traceability:activities.apparel.leather.materialProduction")
  // t("traceability:activities.apparel.leather.tanning")
  // t("traceability:activities.apparel.manufacturing")
  // t("traceability:activities.apparel.yarn.dyeing")
  // t("traceability:activities.apparel.yarn.materialProduction")
  // t("traceability:activities.apparel.yarn.spinning")
  // t("traceability:activities.brand")
  // t("traceability:activities.certifiedEntity")
  // t("traceability:activities.footwear.assembling")
  // t("traceability:activities.footwear.finishing")
  // t("traceability:activities.footwear.stitching")
  // t("traceability:activities.paper.assembling")
  // t("traceability:activities.paper.bleaching")
  // t("traceability:activities.paper.chemicalTreatment")
  // t("traceability:activities.paper.laminating")
  // t("traceability:activities.paper.manufacturing")
  // t("traceability:activities.paper.pressing")
  // t("traceability:activities.paper.printing")
  // t("traceability:activities.paper.resineRemoving")
  // t("traceability:activities.paper.whiteWashing")
  // t("traceability:activities.wood.assembling")
  // t("traceability:activities.wood.brushing")
  // t("traceability:activities.wood.calibrationAndCollage")
  // t("traceability:activities.wood.harvesting")
  // t("traceability:activities.wood.manufacturing")
  // t("traceability:activities.wood.materialDrying")
  // t("traceability:activities.wood.materialPeeking")
  // t("traceability:activities.wood.materialPeeling")
  // t("traceability:activities.wood.materialSawing")
  // t("traceability:activities.wood.sanding")
  // t("traceability:activities.product.washing")
  // t("traceability:activities.product.productWashing")
  // t("traceability:uniqueActivitiesLabels.accessoryProvider")
  // t("traceability:uniqueActivitiesLabels.accessoryRawMaterialProvider")
  // t("traceability:uniqueActivitiesLabels.apparel.duvet.baffle")
  // t("traceability:uniqueActivitiesLabels.apparel.duvet.farm")
  // t("traceability:uniqueActivitiesLabels.apparel.duvet.materialProduction")
  // t("traceability:uniqueActivitiesLabels.apparel.duvet.preProcessor")
  // t("traceability:uniqueActivitiesLabels.apparel.fabric.appret")
  // t("traceability:uniqueActivitiesLabels.apparel.fabric.coating")
  // t("traceability:uniqueActivitiesLabels.apparel.fabric.dyeing")
  // t("traceability:uniqueActivitiesLabels.apparel.fabric.lamination")
  // t("traceability:uniqueActivitiesLabels.apparel.fabric.materialProduction")
  // t("traceability:uniqueActivitiesLabels.apparel.fabric.spinning")
  // t("traceability:uniqueActivitiesLabels.apparel.fabric.weavingKnitting")
  // t("traceability:uniqueActivitiesLabels.apparel.leather.appret")
  // t("traceability:uniqueActivitiesLabels.apparel.leather.dyeing")
  // t("traceability:uniqueActivitiesLabels.apparel.leather.farm")
  // t("traceability:uniqueActivitiesLabels.apparel.leather.materialProduction")
  // t("traceability:uniqueActivitiesLabels.apparel.leather.tanning")
  // t("traceability:uniqueActivitiesLabels.apparel.manufacturing")
  // t("traceability:uniqueActivitiesLabels.apparel.yarn.dyeing")
  // t("traceability:uniqueActivitiesLabels.apparel.yarn.materialProduction")
  // t("traceability:uniqueActivitiesLabels.apparel.yarn.spinning")
  // t("traceability:uniqueActivitiesLabels.brand")
  // t("traceability:uniqueActivitiesLabels.certifiedEntity")
  // t("traceability:uniqueActivitiesLabels.footwear.assembling")
  // t("traceability:uniqueActivitiesLabels.footwear.finishing")
  // t("traceability:uniqueActivitiesLabels.footwear.stitching")
  // t("traceability:uniqueActivitiesLabels.paper.assembling")
  // t("traceability:uniqueActivitiesLabels.paper.bleaching")
  // t("traceability:uniqueActivitiesLabels.paper.chemicalTreatment")
  // t("traceability:uniqueActivitiesLabels.paper.laminating")
  // t("traceability:uniqueActivitiesLabels.paper.manufacturing")
  // t("traceability:uniqueActivitiesLabels.paper.pressing")
  // t("traceability:uniqueActivitiesLabels.paper.printing")
  // t("traceability:uniqueActivitiesLabels.paper.resineRemoving")
  // t("traceability:uniqueActivitiesLabels.paper.whiteWashing")
  // t("traceability:uniqueActivitiesLabels.wood.assembling")
  // t("traceability:uniqueActivitiesLabels.wood.brushing")
  // t("traceability:uniqueActivitiesLabels.wood.calibrationAndCollage")
  // t("traceability:uniqueActivitiesLabels.wood.harvesting")
  // t("traceability:uniqueActivitiesLabels.wood.manufacturing")
  // t("traceability:uniqueActivitiesLabels.wood.materialDrying")
  // t("traceability:uniqueActivitiesLabels.wood.materialPeeking")
  // t("traceability:uniqueActivitiesLabels.wood.materialPeeling")
  // t("traceability:uniqueActivitiesLabels.wood.materialSawing")
  // t("traceability:uniqueActivitiesLabels.wood.sanding")
  // t("traceability:uniqueActivitiesLabels.product.washing")
  // t("traceability:uniqueActivitiesLabels.product.productWashing")
  // t("traceability:uniqueActivitiesLabels.noActivity")
  // t("traceability:activities.noActivity")

  void t
}
